import { useEffect, useState } from "react";
import { Image, BedDouble, Newspaper, Star, Inbox, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";

export default function DashboardTab({ goTab }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/hero-slides"),
      api.get("/admin/villas"),
      api.get("/admin/posts"),
      api.get("/admin/reviews"),
      api.get("/admin/inquiries/list"),
    ])
      .then(([s, v, p, r, q]) =>
        setStats({
          slides: s.data.length,
          villas: v.data.length,
          posts: p.data.length,
          reviewsPending: r.data.filter((x) => !x.approved).length,
          inquiries: q.data.length,
          inquiriesUnreplied: q.data.filter((x) => !x.replied).length,
          latest: q.data.slice(0, 3),
        })
      )
      .catch(() => setStats({ error: true }));
  }, []);

  const cards = stats && !stats.error ? [
    { label: "Hero Slides", value: stats.slides, icon: Image, tab: "hero" },
    { label: "Villas", value: stats.villas, icon: BedDouble, tab: "villas" },
    { label: "Blog Posts", value: stats.posts, icon: Newspaper, tab: "blog" },
    { label: "Pending Reviews", value: stats.reviewsPending, icon: Star, tab: "reviews" },
    {
      label: "Inquiries",
      value: stats.inquiries,
      icon: Inbox,
      tab: "inquiries",
      sub: stats.inquiriesUnreplied > 0 ? `${stats.inquiriesUnreplied} belum dibalas` : "Semua sudah dibalas",
      subAlert: stats.inquiriesUnreplied > 0,
    },
  ] : [];

  return (
    <div data-testid="dashboard-tab">
      <h2 className="font-serif text-2xl text-[#2C2825] mb-1">Dashboard</h2>
      <p className="text-sm text-[#A0988E] mb-8">Welcome to the Nusadira content console.</p>

      {!stats ? (
        <p className="text-sm text-[#A0988E]">Loading...</p>
      ) : stats.error ? (
        <p className="text-sm text-[#A0988E]">Could not load stats.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            {cards.map((c) => (
              <button
                key={c.label}
                data-testid={`dashboard-card-${c.tab}`}
                onClick={() => goTab(c.tab)}
                className="bg-white border border-[#E5DED4] rounded-md p-5 text-left hover:border-[#9A7B66] transition-colors group"
              >
                <c.icon className="w-5 h-5 text-[#9A7B66] mb-3" />
                <div className="font-serif text-3xl text-[#2C2825]">{c.value}</div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#A0988E] font-sans font-semibold mt-1 group-hover:text-[#9A7B66] transition-colors">
                  {c.label}
                </div>
                {c.sub && (
                  <div
                    data-testid={`dashboard-card-${c.tab}-sub`}
                    className={`text-[11px] font-sans font-semibold mt-1.5 ${c.subAlert ? "text-[#C58A2D]" : "text-green-700"}`}
                  >
                    {c.sub}
                  </div>
                )}
              </button>
            ))}
          </div>

          <h3 className="font-serif text-lg text-[#2C2825] mb-4">Latest Inquiries</h3>
          {stats.latest.length === 0 ? (
            <p className="text-sm text-[#A0988E]">No inquiries yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.latest.map((q) => (
                <button
                  key={q.id}
                  data-testid={`dashboard-inquiry-${q.id}`}
                  onClick={() => goTab("inquiries")}
                  className="w-full flex items-center gap-4 bg-white border border-[#E5DED4] rounded-md p-4 text-left hover:border-[#9A7B66] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-sans font-semibold text-sm text-[#2C2825]">{q.name}</span>
                    <span className="text-xs text-[#A0988E] ml-3">{q.inquiry_type}</span>
                    <span
                      data-testid={`dashboard-inquiry-status-${q.id}`}
                      className={`ml-3 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                        q.replied ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {q.replied ? "Replied" : "Belum dibalas"}
                    </span>
                    <p className="text-xs text-[#6E675F] truncate mt-1">{q.message}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#9A7B66] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
